import { Button, ButtonProps } from "../Button";
import { useCalendar } from "./CalendarContext";
import { forwardRef } from "react";
import IconKeyboardArrowDown from "@servicetitan/hammer-icon/mdi/round/keyboard_arrow_down.svg";
import IconKeyboardArrowUp from "@servicetitan/hammer-icon/mdi/round/keyboard_arrow_up.svg";
import { DateTime } from "luxon";

export type CalendarYearButtonProps = ButtonProps & {
  /**
   * The current selection state of the calendar.
   */
  calendarSelectionState: "day" | "month" | "year";
};

/**
 * A button that displays the current year and allows the user to select a different year.
 *
 * @param props.calendarSelectionState - The current selection state of the calendar.
 * @param props - The rest of the props for the Button component.
 * @returns A button that displays the current year and allows the user to select a different year.
 */
export const CalendarYearButton = forwardRef<
  HTMLButtonElement,
  CalendarYearButtonProps
>((props, ref) => {
  const { calendarSelectionState, ...rest } = props;
  const { selectedYear, locale } = useCalendar();

  const currentYear = DateTime.fromObject({ year: selectedYear })
    .setLocale(locale)
    .toLocaleString({ year: "numeric" });

  return (
    <Button
      ref={ref}
      appearance="ghost"
      size="small"
      icon={{
        after:
          calendarSelectionState === "year"
            ? IconKeyboardArrowUp
            : IconKeyboardArrowDown,
      }}
      {...rest}
    >
      {currentYear}
    </Button>
  );
});

CalendarYearButton.displayName = "CalendarYearButton";
